import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChevronDown, ChevronRight, Info, Paperclip, AlertTriangle, ExternalLink } from 'lucide-react';
import type { DepreciationItem, ConfidenceLevel } from '@/types/depreciation';
import { formatCurrency } from '@/lib/format-currency';

interface BatchDetailGridProps {
  items: DepreciationItem[];
  onUpdateItem: (itemId: string, updates: Partial<DepreciationItem>) => void;
  readonly?: boolean;
  selectedItemIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
}

export function BatchDetailGrid({ items, onUpdateItem, readonly = false, selectedItemIds = new Set(), onSelectionChange }: BatchDetailGridProps): React.ReactElement {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<DepreciationItem | null>(null);
  const [overrideReason, setOverrideReason] = useState('');

  const toggleRow = (itemId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      onSelectionChange(new Set(items.map(item => item.id)));
    } else {
      onSelectionChange(new Set());
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (!onSelectionChange) return;
    const newSelection = new Set(selectedItemIds);
    if (checked) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    onSelectionChange(newSelection);
  };

  const handlePercentChange = (item: DepreciationItem, newPercent: string) => {
    const percent = parseFloat(newPercent);
    if (isNaN(percent)) return;

    const newValue = (item.openingNBV * percent) / 100;
    const newClosingNBV = Math.max(item.openingNBV - newValue, item.residualValue);

    onUpdateItem(item.id, {
      depreciationPercent: percent,
      depreciationValue: newValue,
      closingNBV: newClosingNBV,
      isManualOverride: false,
    });
  };

  const handleValueChange = (item: DepreciationItem, newValue: string) => {
    const value = parseFloat(newValue);
    if (isNaN(value)) return;

    const newPercent = (value / item.openingNBV) * 100;
    const newClosingNBV = Math.max(item.openingNBV - value, item.residualValue);

    onUpdateItem(item.id, {
      depreciationPercent: newPercent,
      depreciationValue: value,
      closingNBV: newClosingNBV,
      isManualOverride: false,
    });
  };

  const confirmOverride = () => {
    if (!currentItem || !overrideReason.trim()) return;

    const item = currentItem as any;
    onUpdateItem(currentItem.id, {
      depreciationPercent: item._pendingPercent,
      depreciationValue: item._pendingValue,
      closingNBV: item._pendingClosingNBV,
      isManualOverride: true,
      overrideReason: overrideReason,
      overrideBy: 'Current User',
      overrideDate: new Date().toISOString(),
    });

    setOverrideDialogOpen(false);
    setOverrideReason('');
    setCurrentItem(null);
  };

  const getConfidenceBadge = (confidence: ConfidenceLevel) => {
    const variants = {
      High: 'default',
      Medium: 'secondary',
      Low: 'destructive',
    } as const;

    return (
      <Badge variant={variants[confidence]} className="text-xs">
        {confidence}
      </Badge>
    );
  };

  return (
    <>
      <div className="border overflow-hidden bg-white dark:bg-black">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {!readonly && onSelectionChange && (
                  <TableHead className="w-[40px] pl-6">
                    <Checkbox
                      checked={selectedItemIds.size === items.length && items.length > 0}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                )}
                <TableHead className="w-[40px]"></TableHead>
                <TableHead className="w-[120px]">Asset Code</TableHead>
                <TableHead className="min-w-[200px]">Asset Description</TableHead>
                <TableHead className="text-right w-[140px]">Opening NBV (£)</TableHead>
                <TableHead className="w-[120px]">Method</TableHead>
                <TableHead className="text-right w-[130px]">Life Remaining</TableHead>
                <TableHead className="text-right w-[140px]">Depreciation %</TableHead>
                <TableHead className="text-right w-[180px]">Depreciation Value (£)</TableHead>
                <TableHead className="text-right w-[140px]">Closing NBV (£)</TableHead>
                <TableHead className="w-[100px] pr-6">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <>
                  <TableRow key={item.id}>
                    {!readonly && onSelectionChange && (
                      <TableCell className="w-[40px] pl-6">
                        <Checkbox
                          checked={selectedItemIds.has(item.id)}
                          onCheckedChange={(checked) => handleSelectItem(item.id, checked as boolean)}
                          aria-label={`Select ${item.assetCode}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="w-[40px]">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleRow(item.id)}
                        className="h-6 w-6 p-0"
                      >
                        {expandedRows.has(item.id) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="font-medium w-[120px]">{item.assetCode}</TableCell>
                    <TableCell className="min-w-[200px]">
                      {item.assetDescription}
                    </TableCell>
                    <TableCell className="text-right w-[140px]">{formatCurrency(item.openingNBV)}</TableCell>
                    <TableCell className="w-[120px]">{item.method}</TableCell>
                    <TableCell className="text-right w-[130px]">{item.lifeRemaining.toFixed(2)} yrs</TableCell>
                    <TableCell className="w-[140px]">
                      {readonly ? (
                        <div className="text-right">{formatCurrency(item.depreciationPercent || 0)}%</div>
                      ) : (
                        <Input
                          type="number"
                          step="0.01"
                          value={item.depreciationPercent?.toFixed(2) || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handlePercentChange(item, e.target.value)}
                          className="text-right"
                        />
                      )}
                    </TableCell>
                    <TableCell className="w-[180px]">
                      {readonly ? (
                        <div className="text-right">{formatCurrency(item.depreciationValue)}</div>
                      ) : (
                        <Input
                          type="number"
                          step="0.01"
                          value={item.depreciationValue.toFixed(2)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange(item, e.target.value)}
                          className="text-right"
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium w-[140px]">{formatCurrency(item.closingNBV)}</TableCell>
                    <TableCell className="w-[100px]">
                      <div className="flex items-center gap-2">
                        {item.notes && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{item.notes}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {item.attachments && item.attachments.length > 0 && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Paperclip className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{item.attachments.length} attachment(s)</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {item.validationStatus === 'error' && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertTriangle className="h-4 w-4 text-destructive" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="space-y-1">
                                  {item.validationMessages?.map((msg, i) => (
                                    <p key={i}>{msg}</p>
                                  ))}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row - Explainability */}
                  {expandedRows.has(item.id) && (
                    <TableRow>
                      <TableCell colSpan={11} className="bg-muted/50">
                        <div className="p-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Calculation Inputs */}
                            <div className="space-y-3">
                              <h4 className="font-semibold flex items-center gap-2">
                                <Info className="h-4 w-4" />
                                Why this value?
                              </h4>
                              {item.calculationInputs && (
                                <div className="space-y-2 text-sm">
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="text-muted-foreground">Method:</div>
                                    <div>{item.calculationInputs.projectMethod}</div>
                                    <div className="text-muted-foreground">Useful Life:</div>
                                    <div>{item.calculationInputs.usefulLife} years</div>
                                    <div className="text-muted-foreground">Residual Value:</div>
                                    <div>£{formatCurrency(item.calculationInputs.residualValue)}</div>
                                    <div className="text-muted-foreground">Pro-rata Rule:</div>
                                    <div>{item.calculationInputs.proRataConvention}</div>
                                    <div className="text-muted-foreground">Start Trigger:</div>
                                    <div>{item.calculationInputs.startDateRule}</div>
                                    <div className="text-muted-foreground">In-Service Date:</div>
                                    <div>{new Date(item.calculationInputs.inServiceDate).toLocaleDateString('en-GB')}</div>
                                  </div>
                                  <div className="pt-2 border-t">
                                    <div className="text-muted-foreground mb-1">Formula:</div>
                                    <code className="text-xs bg-muted px-2 py-1 rounded">
                                      {item.calculationInputs.formula}
                                    </code>
                                  </div>
                                  <div className="pt-2">
                                    <div className="text-muted-foreground mb-1">Data Sources:</div>
                                    <ul className="list-disc list-inside text-xs space-y-1">
                                      {item.calculationInputs.dataSources.map((source, i) => (
                                        <li key={i}>{source}</li>
                                      ))}
                                    </ul>
                                  </div>
                                  <div className="pt-2">
                                    <div className="text-muted-foreground mb-1">Assumptions:</div>
                                    <ul className="list-disc list-inside text-xs space-y-1">
                                      {item.calculationInputs.assumptions.map((assumption, i) => (
                                        <li key={i}>{assumption}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Prior Periods */}
                            <div className="space-y-3">
                              <h4 className="font-semibold">History (Prior 3 Periods)</h4>
                              {item.priorPeriods && item.priorPeriods.length > 0 ? (
                                <div className="space-y-2">
                                  {item.priorPeriods.map((period, i) => (
                                    <div key={i} className="text-sm border rounded p-2">
                                      <div className="font-medium mb-1">{period.period}</div>
                                      <div className="grid grid-cols-3 gap-2 text-xs">
                                        <div>
                                          <div className="text-muted-foreground">Opening</div>
                                          <div>£{formatCurrency(period.openingNBV)}</div>
                                        </div>
                                        <div>
                                          <div className="text-muted-foreground">Depreciation</div>
                                          <div>£{formatCurrency(period.depreciation)}</div>
                                        </div>
                                        <div>
                                          <div className="text-muted-foreground">Closing</div>
                                          <div>£{formatCurrency(period.closingNBV)}</div>
                                        </div>
                                      </div>
                                      {period.revaluation && (
                                        <div className="mt-1 text-xs text-amber-600">
                                          Revaluation: £{formatCurrency(period.revaluation)}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">No prior period data available</p>
                              )}

                              <Button variant="outline" size="sm" className="w-full mt-4">
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Open Asset in New Tab
                              </Button>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Override Reason Dialog */}
      <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual Override Reason</DialogTitle>
            <DialogDescription>
              Manual override will replace the AI suggestion. Provide a reason for audit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason for Override *</Label>
              <Textarea
                id="reason"
                value={overrideReason}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setOverrideReason(e.target.value)}
                placeholder="Explain why you're overriding the AI suggestion..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmOverride} disabled={!overrideReason.trim()}>
              Confirm Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
